/**
 * SIMG-PATRIMOINE-2026-001 — registre des immobilisations.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  patrimoineApi,
  type CategorieImmobilisation,
  type CreerImmobilisationInput,
  type StatutImmobilisation,
} from '@/api/patrimoine';

const PATRIMOINE_KEY = ['patrimoine'] as const;

export interface FiltresPatrimoine {
  statut?:         StatutImmobilisation;
  categorie?:      CategorieImmobilisation;
  recherche?:      string;
  inclureSorties?: boolean;
}

export const useCategoriesImmobilisation = () =>
  useQuery({
    queryKey: [...PATRIMOINE_KEY, 'categories'],
    queryFn:  patrimoineApi.categories,
    // Un référentiel de six entrées qui ne bouge qu'avec le code : inutile de
    // le redemander à chaque ouverture d'écran.
    staleTime: 60 * 60 * 1000,
  });

export const useSynthesePatrimoine = () =>
  useQuery({
    queryKey: [...PATRIMOINE_KEY, 'synthese'],
    queryFn:  patrimoineApi.synthese,
  });

export const useImmobilisations = (filtres: FiltresPatrimoine = {}) =>
  useQuery({
    queryKey: [...PATRIMOINE_KEY, 'liste', filtres],
    queryFn:  () => patrimoineApi.lister({ ...filtres, limit: 200 }),
  });

export const useImmobilisation = (id: string | null) =>
  useQuery({
    queryKey: [...PATRIMOINE_KEY, 'detail', id],
    queryFn:  () => patrimoineApi.detail(id!),
    enabled:  !!id,
  });

export const usePlanAmortissement = (id: string | null) =>
  useQuery({
    queryKey: [...PATRIMOINE_KEY, 'plan', id],
    queryFn:  () => patrimoineApi.planAmortissement(id!),
    enabled:  !!id,
  });

/** Toute écriture au registre change aussi la synthèse : on rafraîchit tout. */
const useInvalidation = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: PATRIMOINE_KEY });
};

export const useCreerImmobilisation = () => {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (body: CreerImmobilisationInput) => patrimoineApi.creer(body),
    onSuccess:  invalider,
  });
};

export const useAffecterImmobilisation = () => {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; localisation?: string; detenteurNom?: string; statut?: StatutImmobilisation; motif?: string }) =>
      patrimoineApi.affecter(id, body),
    onSuccess: invalider,
  });
};

export const useSortirImmobilisation = () => {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; type: 'cession' | 'rebut'; motif: string; valeurCessionCents?: number }) =>
      patrimoineApi.sortir(id, body),
    onSuccess: invalider,
  });
};

export const useDoterAmortissements = () => {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (periode: string) => patrimoineApi.doter(periode),
    onSuccess:  invalider,
  });
};
